import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableHead, TableRow, TableCell,
  TableBody, IconButton, Tooltip, Button, Alert, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Divider, Switch,
  FormControlLabel, Grid, Collapse
} from '@mui/material';
import { Add, Edit, Delete, ArrowBack, Groups, ExpandMore, ExpandLess, WarningAmber } from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import { useLeaveTypes } from '../hooks/useLeaveTypes';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const emptyForm = { groupCode: '', name: '', description: '', isActive: true };

const LeaveGroupList = () => {
  const navigate = useNavigate();
  const { leaveTypes } = useLeaveTypes();

  const [groups,   setGroups]   = useState([]);
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState('');
  const [open,     setOpen]     = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [form,     setForm]     = useState(emptyForm);
  const { attemptClose, discardDialog } = useUnsavedGuard({ open, value: form, onClose: () => setOpen(false) });
  const [saving,     setSaving]     = useState(false);
  const [formErr,    setFormErr]    = useState('');
  const [confirmDlg, setConfirmDlg] = useState(null);
  const [expanded, setExpanded] = useState(null); // group code of expanded row

  // Entitlement rows in dialog
  const [entitlements, setEntitlements] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/leave/groups/all`, { headers: hdrs() });
      setGroups(data);
    } catch { setError('Failed to load leave groups'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setEntitlements([]);
    setFormErr(''); setOpen(true);
  };

  const openEdit = (g) => {
    setEditing(g);
    setForm({
      groupCode: g.groupCode, name: g.name,
      description: g.description || '', isActive: g.isActive !== false,
    });
    setEntitlements((g.entitlements || []).map(e => ({ ...e })));
    setFormErr(''); setOpen(true);
  };

  const handle = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const addEntitlementRow = () => {
    setEntitlements(e => [...e, { leaveTypeCode: '', leaveTypeName: '', daysPerYear: '' }]);
  };

  const updateEntRow = (idx, field, val) => {
    setEntitlements(rows => rows.map((r, i) => {
      if (i !== idx) return r;
      if (field === 'leaveTypeCode') {
        const lt = leaveTypes.find(t => t.code === val);
        return { ...r, leaveTypeCode: val, leaveTypeName: lt?.name || '', daysPerYear: r.daysPerYear || (lt?.daysPerYear ?? '') };
      }
      return { ...r, [field]: val };
    }));
  };

  const removeEntRow = (idx) => setEntitlements(rows => rows.filter((_, i) => i !== idx));

  const handleSave = async () => {
    if (!form.groupCode?.trim()) { setFormErr('Group code is required'); return; }
    if (!form.name?.trim())      { setFormErr('Name is required'); return; }
    const cleanEnts = entitlements
      .filter(e => e.leaveTypeCode && e.daysPerYear !== '')
      .map(e => ({ ...e, daysPerYear: Number(e.daysPerYear) }));
    setSaving(true); setFormErr('');
    try {
      const body = { ...form, entitlements: cleanEnts };
      if (editing) {
        await axios.put(`${API}/api/leave/groups/${editing.groupCode}`, body, { headers: hdrs() });
      } else {
        await axios.post(`${API}/api/leave/groups`, body, { headers: hdrs() });
      }
      setOpen(false);
      setSuccess(editing ? 'Updated' : 'Created');
      setTimeout(() => setSuccess(''), 3000);
      load();
    } catch (err) { setFormErr(err.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const confirmAction = async () => {
    const g = confirmDlg;
    setConfirmDlg(null);
    try {
      await axios.delete(`${API}/api/leave/groups/${g.groupCode}`, { headers: hdrs() });
      setSuccess('Deleted'); load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.error || 'Delete failed'); }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4, mb: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Button startIcon={<ArrowBack />} size="small" onClick={() => navigate('/hr/leave')}>Back</Button>
              <Groups sx={{ color: '#6a1b9a' }} />
              <Typography variant="h5" fontWeight={700} color="#6a1b9a">Leave Groups</Typography>
              <Chip label={`${groups.filter(g => g.isActive !== false).length} active`} size="small" color="success" />
            </Box>
            <Button variant="contained" startIcon={<Add />} onClick={openAdd}
              sx={{ bgcolor: '#6a1b9a', '&:hover': { bgcolor: '#4a148c' } }}>
              Add Group
            </Button>
          </Box>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

          <Divider sx={{ mb: 2 }} />

          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Code</strong></TableCell>
                <TableCell><strong>Name / Description</strong></TableCell>
                <TableCell align="center"><strong>Entitlements</strong></TableCell>
                <TableCell align="center"><strong>Status</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary" py={3}>
                      No leave groups. Defaults are seeded on first use.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : groups.map(g => (
                <React.Fragment key={g.groupCode}>
                  <TableRow hover sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' } }}>
                    <TableCell>
                      <Chip label={g.groupCode} size="small" variant="outlined"
                        sx={{ fontFamily: 'monospace', fontWeight: 700 }} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}
                        color={g.isActive === false ? 'text.disabled' : 'inherit'}>
                        {g.name}
                      </Typography>
                      {g.description && (
                        <Typography variant="caption" color="text.secondary">{g.description}</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {(g.entitlements?.length > 0) ? (
                        <Button size="small" variant="text"
                          endIcon={expanded === g.groupCode ? <ExpandLess /> : <ExpandMore />}
                          onClick={() => setExpanded(expanded === g.groupCode ? null : g.groupCode)}>
                          {g.entitlements.length} override{g.entitlements.length !== 1 ? 's' : ''}
                        </Button>
                      ) : (
                        <Typography variant="caption" color="text.secondary">— uses defaults</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={g.isActive !== false ? 'Active' : 'Inactive'} size="small"
                        color={g.isActive !== false ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" color="primary" onClick={() => openEdit(g)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => setConfirmDlg(g)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  {/* Entitlements expansion */}
                  {expanded === g.groupCode && (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ py: 0, bgcolor: '#f9f9f9' }}>
                        <Collapse in timeout="auto">
                          <Box sx={{ py: 1.5, px: 3 }}>
                            <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase">
                              Entitlement Overrides for {g.name}
                            </Typography>
                            <Table size="small" sx={{ mt: 1 }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell><strong>Leave Type</strong></TableCell>
                                  <TableCell align="right"><strong>Days / Year</strong></TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {g.entitlements.map((e, i) => (
                                  <TableRow key={i}>
                                    <TableCell>{e.leaveTypeName || e.leaveTypeCode}</TableCell>
                                    <TableCell align="right"><strong>{e.daysPerYear}</strong></TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Confirm Delete Dialog */}
      <Dialog open={Boolean(confirmDlg)} onClose={() => setConfirmDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningAmber color="error" /> Delete Leave Group
        </DialogTitle>
        <DialogContent>
          <Typography>
            Delete leave group <strong>{confirmDlg?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDlg(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmAction}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog ── */}
      {discardDialog}
      <Dialog open={open} onClose={attemptClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? `Edit — ${editing.name}` : 'Add Leave Group'}</DialogTitle>
        <DialogContent dividers>
          {formErr && <Alert severity="error" sx={{ mb: 2 }}>{formErr}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField label="Group Code *" name="groupCode" value={form.groupCode}
                onChange={e => setForm(f => ({ ...f, groupCode: e.target.value.toUpperCase() }))}
                size="small" fullWidth disabled={!!editing}
                helperText={editing ? 'Cannot change' : 'e.g. TEACH'}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={8}>
              <TextField label="Name *" name="name" value={form.name}
                onChange={handle} size="small" fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Description" name="description" value={form.description}
                onChange={handle} size="small" fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>

            {/* Entitlement overrides */}
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase">
                  Entitlement Overrides (optional)
                </Typography>
                <Button size="small" startIcon={<Add />} onClick={addEntitlementRow}>Add Row</Button>
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                Leave blank to use defaults from each Leave Type. Add overrides only where this group differs.
              </Typography>
              {entitlements.map((row, idx) => (
                <Box key={idx} display="flex" gap={1} mb={1} alignItems="center">
                  <TextField select label="Leave Type" value={row.leaveTypeCode} size="small"
                    sx={{ flex: 2 }} InputLabelProps={{ shrink: true }}
                    SelectProps={{ displayEmpty: true }}
                    onChange={e => updateEntRow(idx, 'leaveTypeCode', e.target.value)}>
                    <MenuItem value=""><em>— Select —</em></MenuItem>
                    {leaveTypes.map(lt => (
                      <MenuItem key={lt.code} value={lt.code}>{lt.name}</MenuItem>
                    ))}
                  </TextField>
                  <TextField label="Days/Year" value={row.daysPerYear} size="small" type="number"
                    sx={{ flex: 1 }} InputLabelProps={{ shrink: true }}
                    onChange={e => updateEntRow(idx, 'daysPerYear', e.target.value)} />
                  <IconButton size="small" color="error" onClick={() => removeEntRow(idx)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Grid>

            {editing && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={form.isActive !== false}
                    onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />}
                  label="Active" />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ bgcolor: '#6a1b9a', '&:hover': { bgcolor: '#4a148c' } }}>
            {saving ? 'Saving…' : editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeaveGroupList;
